import isEmpty from 'lodash/isEmpty';
import { useMemo } from 'react';
import { useAppSetting } from './useAppSetting';

const sortVendorAfterPreSelect = (vendors: VendorInfo[], preSelectedVendor: VendorInfo) => {
  return [preSelectedVendor, ...vendors.filter((vendor) => vendor.VendorOrgId !== preSelectedVendor.VendorOrgId)];
};

const filterVendorByExactName = (vendors: VendorInfo[], vendorOrgName: string) => {
  const vendorByNames = vendors.filter(
    (vendor) =>
      vendor.VendorOrgName.trim().toLowerCase() === vendorOrgName.trim().toLowerCase() ||
      vendor.OrgHistoricalCompanyNames.some((name) => name.trim().toLowerCase() === vendorOrgName.trim().toLowerCase())
  );
  if (vendorByNames.length <= 1) return vendorByNames[0];

  if (vendorByNames.length > 1) {
    const sortedByNumberOfAssessment = vendorByNames.sort((a, b) => b.AssessmentsCount - a.AssessmentsCount);
    return sortedByNumberOfAssessment[0];
  }

  return null;
};

const filterVendorByExactDomain = (vendors: VendorInfo[], domainName: string) => {
  const vendorByDomain = vendors.filter(
    (vendor) =>
      vendor.DomainName?.trim().toLowerCase() === domainName.trim().toLowerCase() ||
      vendor.AdditionalDomainNames.some((domain) => domain.trim().toLowerCase() === domainName.trim().toLowerCase())
  );
  if (vendorByDomain.length <= 1) return vendorByDomain[0];

  if (vendorByDomain.length > 1) {
    const sortedByNumberOfAssessment = vendorByDomain.sort((a, b) => b.AssessmentsCount - a.AssessmentsCount);
    return sortedByNumberOfAssessment[0];
  }

  return null;
};

const filterBySimilarityScore = (vendors: VendorInfo[], threshold: number) => {
  const filteredVendors = vendors.filter((vendor) => vendor.Score >= threshold);
  if (isEmpty(filteredVendors)) return null;
  const highestSimilarityScore = Math.max(...filteredVendors.map((vendor) => vendor.Score));
  const isMultipleHighestScore = filteredVendors.filter((vendor) => vendor.Score === highestSimilarityScore).length > 1;
  if (isMultipleHighestScore) {
    const sortedByNumberOfAssessment = filteredVendors.sort((a, b) => b.AssessmentsCount - a.AssessmentsCount);
    return sortedByNumberOfAssessment[0];
  }
  return filteredVendors[0];
};

const limitDisplayThreshold = (vendors: VendorInfo[], displayThreshold: number) => {
  if (displayThreshold <= 0) return vendors;
  return vendors.filter((vendor) => vendor.Score >= displayThreshold);
};

export function usePreSelectVendor({
  vendors,
  vendorOrgName,
  domainName,
  excludeAssociatedVendor = false,
  filterByDisplayThreshold = true,
}: Readonly<UsePreSelectVendorProps>) {
  const preSelectThresholdFetcher = useAppSetting<AppSettingsResponse>(['VendorSimilarityScore_PreSelectThreshold', 'VendorSimilarityScore_DisplayThreshold']);

  const preSelectThreshold = Number(preSelectThresholdFetcher?.data?.VendorSimilarityScore_PreSelectThreshold ?? 0);

  const displayThreshold = useMemo(() => {
    if (!filterByDisplayThreshold) return 0;
    return Number(preSelectThresholdFetcher?.data?.VendorSimilarityScore_DisplayThreshold ?? 0);
  }, [filterByDisplayThreshold, preSelectThresholdFetcher?.data?.VendorSimilarityScore_DisplayThreshold]);

  const mostMatchedVendor: [VendorInfo | null, VendorInfo[]] = useMemo(() => {
    if (isEmpty(vendors)) return [null, vendors];
    if (isEmpty(vendorOrgName) && isEmpty(domainName)) return [null, limitDisplayThreshold(vendors, displayThreshold)];

    const sortedBySimilarity = [...vendors].sort((a, b) => b.Score - a.Score);

    let listVendorToFilter = sortedBySimilarity;
    if (excludeAssociatedVendor) {
      listVendorToFilter = sortedBySimilarity.filter((vendor) => !vendor.IsAssociatedWithCustomer);
    }

    if (vendorOrgName) {
      const matchedName = filterVendorByExactName(listVendorToFilter, vendorOrgName);
      if (matchedName) return [matchedName, limitDisplayThreshold(sortVendorAfterPreSelect(vendors, matchedName), displayThreshold)];
    }

    if (domainName) {
      const matchedDomain = filterVendorByExactDomain(listVendorToFilter, domainName);
      if (matchedDomain) return [matchedDomain, limitDisplayThreshold(sortVendorAfterPreSelect(vendors, matchedDomain), displayThreshold)];
    }

    const highestSimilarityVendor = sortedBySimilarity[0];
    if (highestSimilarityVendor.Score >= preSelectThreshold) {
      const matchedSimilarityScore = filterBySimilarityScore(listVendorToFilter, preSelectThreshold);
      if (matchedSimilarityScore)
        return [matchedSimilarityScore, limitDisplayThreshold(sortVendorAfterPreSelect(vendors, matchedSimilarityScore), displayThreshold)];
    }

    return [null, limitDisplayThreshold(vendors, displayThreshold)];
  }, [vendors, vendorOrgName, domainName, preSelectThreshold]);

  return mostMatchedVendor;
}

// ==============================================================

type UsePreSelectVendorProps = {
  vendors: VendorInfo[];
  vendorOrgName?: string;
  domainName?: string;
  excludeAssociatedVendor?: boolean;
  filterByDisplayThreshold?: boolean;
};
type VendorInfo = {
  VendorOrgId: number;
  VendorOrgName: string;
  DomainName?: string;
  Description: string;
  RegisteredBusinessAddressCountry: string;
  RegisteredBusinessAddressState?: string;
  OrgHistoricalCompanyNames: string[];
  AdditionalDomainNames: string[];
  Sectors: string[];
  Score: number;
  IsAssociatedWithCustomer: boolean;
  AssessmentsCount: number;
  MostRecentAssessmentStartDate: string;
};

type AppSettingsResponse = {
  VendorSimilarityScore_PreSelectThreshold: string;
  VendorSimilarityScore_DisplayThreshold: string;
};
